import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {CheckBox, Flex, Text, ToggleSwitch} from '@priority-software/priority-style-react';
import cx from 'classnames';
import {GridContainer} from '../../../../../../common/GridLayout/GridLayout';
import {SelectControl} from '../../../../AutomationRuleSelect/SelectControl';
import {AlertFields} from '../../../../../../../enums';
import {AutomationChoose} from '../../../../../../../reducer/model';
import {templateValueSeparator} from './constants';
import {createSelectTemplateValue} from './utils';
import {texts} from '../../../../../../../texts';
import styles from './styles.module.scss';

type Props = {
    componentClasses?: string;
    document?: number;
    documentFormat?: number;
    isWord?: boolean;
    documentOptions: Array<AutomationChoose>;
    isPDF?: boolean;
    fromAttachment?: boolean;
    useWordTemplate?: boolean;
    updateRuleField: (value: string | number | boolean | Array<string>, field: string) => void;
};

type DocumentTemplatesData = {
    printTemplates?: Array<AutomationChoose>;
    printFormats?: Array<AutomationChoose>;
};

export const AttachmentSection = (props: Props) => {
    const {
        isPDF,
        isWord,
        document,
        fromAttachment,
        componentClasses,
        documentOptions,
        documentFormat,
        updateRuleField
    } = props;
    const [expandAttachment, setExpandAttachment] = useState(Boolean(document));
    const [selectedDocumentOption, setSelectedDocumentOption] = useState(
        documentOptions.find(option => option.value === document) || null
    );

    const clickedExpandedBtn = useRef(false);
    const firstRowRef = useRef<HTMLDivElement>();

    const disableDocumentsCheckbox = Boolean(!document);
    const disableFormats = Boolean(!document);

    useEffect(() => {
        if (expandAttachment && clickedExpandedBtn.current) {
            window.requestAnimationFrame(() => {
                firstRowRef.current.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                });
            });
        }
    }, [expandAttachment]);

    const toggleAttachmentSection = useCallback(() => {
        setExpandAttachment(expanded => !expanded);
        // if the user click expand attachments we will trigger smooth scroll in the useEffect
        clickedExpandedBtn.current = true;
    }, []);

    const handleDocuments = useCallback(
        (option: AutomationChoose, name: string) => {
            updateRuleField(option?.value, name);
            updateRuleField(undefined, AlertFields.DocumentFormat);
            setSelectedDocumentOption(option);
        },
        [updateRuleField]
    );

    const handleTemplates = useCallback(
        (option: AutomationChoose, name: string) => {
            const [value, isWord] = ((option.value as string) || '').split(templateValueSeparator);

            updateRuleField(Number(value), name);
            updateRuleField(isWord === 'true', AlertFields.WordTemplates);
        },
        [updateRuleField]
    );

    const templateOptions = useMemo(() => {
        if (selectedDocumentOption) {
            const extraData = (selectedDocumentOption.data as DocumentTemplatesData) || {};

            return [
                {
                    label: texts.html_group_label,
                    value: '',
                    groupOptions: extraData.printFormats.map(item => ({
                        ...item,
                        value: createSelectTemplateValue(item.value as number, false)
                    }))
                },
                {
                    label: texts.doc_group_label,
                    value: '',
                    groupOptions: extraData.printTemplates.map(item => ({
                        ...item,
                        value: createSelectTemplateValue(item.value as number, true)
                    }))
                }
            ].filter(item => !!item.groupOptions.length);
        }
        return [];
    }, [selectedDocumentOption]);

    const mappedDocumentFormatValue = useMemo(() => {
        if (documentFormat) {
            /*
             * we should create some `computed` value based option.value and `isWord` template value,
             * because some HTML options and Word options might have the same value in the DB, i.e. `-101`
             * and the only difference to separate them is taking into account `isWord` flag.
             */
            return createSelectTemplateValue(documentFormat, isWord);
        }
        return undefined;
    }, [documentFormat, isWord]);

    const clearValue = useCallback(
        (name: string) => {
            updateRuleField(undefined, name);
            if (name === AlertFields.Document) {
                updateRuleField(undefined, AlertFields.DocumentFormat);
            } else if (name === AlertFields.DocumentFormat) {
                updateRuleField(undefined, AlertFields.WordTemplates);
            }
        },
        [updateRuleField]
    );

    return (
        <div className={cx(styles.component, componentClasses)} ref={firstRowRef}>
            <Flex componentClasses={styles.row}>
                <Text tag="div" skin="paragraph-2">
                    {texts.add_document}
                </Text>
                <ToggleSwitch onChange={toggleAttachmentSection} checked={expandAttachment} />
            </Flex>

            {expandAttachment && (
                <>
                    <div className={styles.row}>
                        <GridContainer columnCount={2}>
                            <div className={styles.rowItem}>
                                <Text tag="label" skin="paragraph-2" textColor="primary-700">
                                    {texts.documents_label}
                                </Text>

                                <SelectControl
                                    name={AlertFields.Document}
                                    defaultValue={document ? document : undefined}
                                    onChange={handleDocuments}
                                    options={documentOptions}
                                    menuScrollMaxHeight={300}
                                    clearValue={clearValue}
                                    clearable
                                />
                            </div>

                            <div className={styles.rowItem}>
                                <Text
                                    tag="label"
                                    skin="paragraph-2"
                                    textColor={disableFormats ? 'secondary-400' : 'primary-700'}
                                >
                                    {texts.print_format_label}
                                </Text>

                                <SelectControl
                                    key={document?.toString()}
                                    isDisabled={disableFormats}
                                    name={AlertFields.DocumentFormat}
                                    defaultValue={mappedDocumentFormatValue}
                                    onChange={handleTemplates}
                                    options={templateOptions}
                                    menuScrollMaxHeight={300}
                                    openingSide="leftSide"
                                    clearValue={clearValue}
                                    clearable
                                    hasSearch
                                />
                            </div>
                        </GridContainer>
                    </div>

                    <div className={styles.row}>
                        <CheckBox
                            disabled={disableDocumentsCheckbox}
                            checked={isPDF}
                            onChange={() => updateRuleField(!isPDF, AlertFields.Pdf)}
                        >
                            <Text tag="span" skin="paragraph-2">
                                {texts.pdf_label}
                            </Text>
                        </CheckBox>
                    </div>

                    <div className={styles.row}>
                        <CheckBox
                            disabled={disableDocumentsCheckbox}
                            checked={fromAttachment}
                            onChange={() => updateRuleField(!fromAttachment, AlertFields.ExtFile)}
                        >
                            <Text tag="span" skin="paragraph-2">
                                {texts.attachment_label}
                            </Text>
                        </CheckBox>
                    </div>
                </>
            )}
        </div>
    );
};
